import { gql, useMutation } from '@apollo/client';
import { Alert, Button, FormControl, FormLabel, Textarea } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useState } from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { PublishModeTab } from '../shared';

const UPLOAD_SCHEMA = gql<
  GraphQLTypes.UploadSchemaDocumentMutation,
  GraphQLTypes.UploadSchemaDocumentMutationVariables
>`
  mutation UploadSchemaDocumentMutation(
    $uploadSchemaTag: String!
    $serviceId: ID!
    $uploadSchemaSchemaDocument: String
  ) {
    service(id: $serviceId) {
      uploadSchema(
        tag: $uploadSchemaTag
        schemaDocument: $uploadSchemaSchemaDocument
      ) {
        success
      }
    }
  }
`;

export const SchemaFileTab = ({
  firstPublish,
  graphId,
  graphVariant,
  document,
  onComplete,
  setTab,
}: {
  firstPublish: boolean;
  graphId: string;
  graphVariant: string;
  document?: string;
  onComplete?: () => void;
  setTab: (tab: PublishModeTab) => void;
}) => {
  const [uploadSchema, { client, loading }] = useMutation(UPLOAD_SCHEMA, {
    onCompleted() {
      client.cache.evict({
        id: client.cache.identify({ __typename: 'Service', id: graphId }),
      });
      client.cache.gc();
    },
  });
  const [manualSDL, setManualSDL] = useState(document || '');
  const [error, setError] = useState('');
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  return (
    <>
      {firstPublish ? (
        <p className="mb-2">
          Pick this option to do a <span className="italic">one-time</span>{' '}
          upload of your schema to get started quickly.
        </p>
      ) : (
        <p className="mb-2">
          Do a <span className="italic">one-time</span> upload of your schema.
        </p>
      )}
      {error && (
        <Alert status="error" className="mb-2">
          {error}
        </Alert>
      )}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggedOver(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDraggedOver(true);
        }}
        onDragEnd={(e) => {
          e.preventDefault();
          setIsDraggedOver(false);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDraggedOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDraggedOver(false);

          if (event.dataTransfer.files) {
            const file = event.dataTransfer.files[0];

            if (file && (file.type.indexOf('text') > -1 || !file.type)) {
              const reader = new FileReader();
              reader.addEventListener('load', function handleFileLoad(e) {
                const data = e.target?.result;
                if (typeof data === 'string') setManualSDL(data);
                if (error) setError('');
                reader.removeEventListener('load', handleFileLoad);
              });
              reader.readAsText(file);
            } else {
              setError(
                'Cannot read this type of file. Please drop a .graphql schema file.',
              );
            }
          }
        }}
      >
        <FormControl>
          <FormLabel>Schema definition</FormLabel>
          <Textarea
            autoFocus
            size="lg"
            className={classnames(
              isDraggedOver && 'border border-focused bg-disabled',
            )}
            placeholder={`# Paste your schema here or drag and drop a .graphql file.
type Query {
    me: User
    products(after: Int = 0, first: Int = 5, type: ProductType): ProductConnection
    topProducts(first: Int = 5): [Product]
}`}
            value={manualSDL}
            onChange={(e) => {
              setManualSDL(e.target.value);
            }}
          />
        </FormControl>
      </div>

      <Alert status="info" className="mt-2">
        To keep things current automatically, follow the instructions in the{' '}
        <ClickableText
          className="font-semibold underline"
          onClick={() => {
            setTab(PublishModeTab.CI);
          }}
        >
          CLI section
        </ClickableText>{' '}
        or use the{' '}
        <ClickableText
          className="font-semibold underline"
          onClick={() => {
            setTab(PublishModeTab.SchemaReporting);
          }}
        >
          Apollo Server integration
        </ClickableText>
        .
      </Alert>

      <div className="mt-8 flex items-end justify-end">
        <div className="flex-1" />
        {onComplete && (
          <div className="flex flex-1 justify-end">
            <Button
              variant="secondary"
              type="submit"
              className="mr-4"
              onClick={onComplete}
            >
              Cancel
            </Button>
          </div>
        )}
        <PermissionWarning
          graphId={graphId}
          graphVariant={graphVariant}
          graphVariantPermissions="canPushSchemas"
          childRenderer={(hasPermission) => (
            <Button
              isLoading={loading}
              isDisabled={!manualSDL || !hasPermission}
              type="submit"
              onClick={() => {
                if (manualSDL) {
                  uploadSchema({
                    variables: {
                      uploadSchemaTag: graphVariant,
                      serviceId: graphId,
                      uploadSchemaSchemaDocument: manualSDL,
                    },
                  })
                    .then(() => {
                      if (onComplete) onComplete();
                    })
                    .catch((e) => {
                      setError(e.message);
                    });
                }
              }}
            >
              Upload
            </Button>
          )}
        />
      </div>
    </>
  );
};
